import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"; // get slug from route
import { fetchPageBySlug, upsertPageBySlug, clearPageState } from "../redux/slices/pageSlice";

// CKEditor
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const EditPageEditor = () => {
    const { slug: routeSlug } = useParams(); // get slug from URL
    const dispatch = useDispatch();
    const { pageData, loading, error, success } = useSelector((state) => state.page);

    const [slug, setSlug] = useState(routeSlug || "");
    const [pageTitle, setPageTitle] = useState("");
    const [pageDesc, setPageDesc] = useState("");
    const [content, setContent] = useState("");
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDescription, setMetaDescription] = useState("");
    const [metaKeywords, setMetaKeywords] = useState("");
    const [relatedButtons, setRelatedButtons] = useState([]);

    // Fetch page data when slug is available
    useEffect(() => {
        if (slug) dispatch(fetchPageBySlug(slug));
        return () => dispatch(clearPageState());
    }, [dispatch, slug]);

    // Prefill form when page data is loaded
    useEffect(() => {
        if (pageData) {
            setPageTitle(pageData.pageTitle || "");
            setPageDesc(pageData.pageDesc || "");
            setContent(pageData.content || "");
            setMetaTitle(pageData.metaTitle || "");
            setMetaDescription(pageData.metaDescription || "");
            setMetaKeywords((pageData.metaKeywords || []).join(", "));
            setRelatedButtons(pageData.relatedButtons || []);
        }
    }, [pageData]);

    // Related Buttons helpers
    const addButton = () => setRelatedButtons([...relatedButtons, { buttonSlug: "", buttonTitle: "" }]);
    const removeButton = (index) => {
        const updated = [...relatedButtons];
        updated.splice(index, 1);
        setRelatedButtons(updated);
    };
    const updateButton = (index, field, value) => {
        const updated = [...relatedButtons];
        updated[index][field] = value;
        setRelatedButtons(updated);
    };

    // Save or update page
    const handleSave = () => {
        if (!slug) return alert("⚠️ Slug is required!");

        dispatch(
            upsertPageBySlug({
                slug,
                pageData: {
                    pageTitle,
                    pageDesc,
                    content,
                    metaTitle,
                    metaDescription,
                    metaKeywords: metaKeywords.split(",").map((kw) => kw.trim()),
                    relatedButtons,
                },
            })
        );
    };

    return (
        <div className="container mt-4">

            <div className="card">
                <div className="card-header">
                    <h4 className="mb-3">{routeSlug ? "Edit Page" : "New Page"}</h4>
                </div>
                <div className="card-body">


                    {/* Slug */}
                    <div className="mb-3">
                        <label className="form-label">Slug</label>
                        <input
                            type="text"
                            className="form-control"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            placeholder="e.g. json-formatter"
                            disabled={!!routeSlug} // prevent changing slug when editing
                        />
                        <small className="text-muted">Slug will be used in the page URL</small>
                    </div>

                    {/* Title */}
                    <div className="mb-3">
                        <label className="form-label">Page Title</label>
                        <input
                            type="text"
                            className="form-control"
                            value={pageTitle}
                            onChange={(e) => setPageTitle(e.target.value)}
                            placeholder="Enter page title"
                        />
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                        <label className="form-label">Page Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={pageDesc}
                            onChange={(e) => setPageDesc(e.target.value)}
                            placeholder="Short description about this page"
                        />
                    </div>

                    {/* Meta Fields */}
                    <div className="mb-3">
                        <label className="form-label">Meta Title</label>
                        <input
                            type="text"
                            className="form-control"
                            value={metaTitle}
                            onChange={(e) => setMetaTitle(e.target.value)}
                            placeholder="Meta Title for SEO"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Meta Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={metaDescription}
                            onChange={(e) => setMetaDescription(e.target.value)}
                            placeholder="Meta Description for SEO"
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Meta Keywords (comma separated)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={metaKeywords}
                            onChange={(e) => setMetaKeywords(e.target.value)}
                            placeholder="json, formatter, tool"
                        />
                    </div>

                    {/* Content */}
                    <div className="mb-3">
                        <label className="form-label">Content</label>
                        <CKEditor
                            editor={ClassicEditor}
                            data={content}
                            onChange={(_, editor) => setContent(editor.getData())}
                        />
                    </div>

                    {/* Related Buttons */}
                    <div className="mb-3">
                        <label className="form-label">Related Buttons</label>
                        {relatedButtons.map((btn, index) => (
                            <div className="d-flex mb-2" key={index}>
                                <input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Button Slug"
                                    value={btn.buttonSlug}
                                    onChange={(e) => updateButton(index, "buttonSlug", e.target.value)}
                                />
                                <input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Button Title"
                                    value={btn.buttonTitle}
                                    onChange={(e) => updateButton(index, "buttonTitle", e.target.value)}
                                />
                                <button type="button" className="btn btn-danger" onClick={() => removeButton(index)}>
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button type="button" className="btn btn-secondary" onClick={addButton}>
                            + Add Related Button
                        </button>
                    </div>

                    {/* Save Button */}
                    <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                        {loading ? "Saving..." : "Save Page"}
                    </button>

                    {success && <p className="text-success mt-2">✅ Page saved successfully!</p>}
                    {error && <p className="text-danger mt-2">{error}</p>}

                </div>
            </div>


        </div>
    );
};

export default EditPageEditor;
